import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

interface InstallButtonProps {
    onDebugClick?: () => void
}

export function InstallButton({ onDebugClick }: InstallButtonProps) {
    const { t } = useTranslation()
    const [deferredPrompt, setDeferredPrompt] = useState<any>(null)
    const [showIOSPrompt, setShowIOSPrompt] = useState(false)
    const [isIOS, setIsIOS] = useState(false)
    const [isInstalled, setIsInstalled] = useState(false)

    useEffect(() => {
        // 检测是否为iOS
        const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
        setIsIOS(iOS)

        // 检测是否已安装
        if (window.matchMedia('(display-mode: standalone)').matches) {
            setIsInstalled(true)
            return
        }

        // Android PWA 安装提示
        const handleBeforeInstallPrompt = (e: Event) => {
            e.preventDefault()
            setDeferredPrompt(e)
        }

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
        }
    }, [])

    const handleInstallClick = async () => {
        if (isIOS) {
            // iOS: 显示提示
            setShowIOSPrompt(true)
        } else if (deferredPrompt) {
            // Android: 触发安装提示
            deferredPrompt.prompt()
            const { outcome } = await deferredPrompt.userChoice
            console.log(`User response to the install prompt: ${ outcome } `)
            setDeferredPrompt(null)
        }
    }

    const showInstallButton = !isInstalled && (isIOS || deferredPrompt)

    // 如果不需要显示安装按钮，且没有 debug 功能，则不渲染
    if (!showInstallButton && !onDebugClick) {
        return null
    }

    return (
        <>
            {/* 桌面端安装按钮 - 仅当需要安装时显示 */}
            {showInstallButton && (
                <button
                    onClick={handleInstallClick}
                    className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-zinc-800/50 hover:bg-zinc-700 text-zinc-300 hover:text-white text-sm font-semibold transition-colors border border-zinc-700"
                    aria-label={t('nav.install_app')}
                >
                    <span className="text-lg">📱</span>
                    <span className="hidden lg:inline">{t('nav.install_app')}</span>
                </button>
            )}

            {/* 移动端按钮 - 优先显示 debug，否则显示安装 */}
            <button
                onClick={onDebugClick ? onDebugClick : handleInstallClick}
                className={`md:hidden w - 9 h - 9 flex items - center justify - center rounded - lg bg - zinc - 800 / 50 hover: bg - zinc - 700 text - zinc - 300 hover: text - white transition - colors border border - zinc - 700 ${ (onDebugClick || showInstallButton) ? '' : 'hidden' } `}
                aria-label={onDebugClick ? "Debug Fill" : t('nav.install_app')}
            >
                {onDebugClick ? '🐞' : '📱'}
            </button>

            {/* iOS 提示弹窗 */}
            {showIOSPrompt && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowIOSPrompt(false)}>
                    <div className="bg-zinc-900 border-2 border-brand-orange rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
                        <div className="text-center">
                            <div className="text-5xl mb-4">📱</div>
                            <h3 className="text-2xl font-bold text-white mb-2">
                                {t('nav.add_to_home')}
                            </h3>
                            <p className="text-zinc-300 mb-6 text-sm leading-relaxed">
                                {t('nav.install_prompt_ios')}
                            </p>
                            <button
                                onClick={() => setShowIOSPrompt(false)}
                                className="btn btn-primary w-full"
                            >
                                {t('game.close')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
